import { action, computed, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { EVENTS } from 'src/constants/events';
import { requests } from 'src/requests';
import client from 'src/services/client';
import cookies from 'src/services/cookie';
import logger from 'src/services/logger';
import { TLocale } from 'src/types/main';
import { logError } from 'src/utils/helpers';
import { productsStore } from '../productsStore';
import moment from 'moment';
import 'moment/locale/ar';

// Display english figures instead arabic, 1-10 instead ١-١٠
moment?.defineLocale('ar-custom', {
  parentLocale: 'ar',
  preparse: (string: string) => string,
  postformat: (string: string) => string,
  week: {
    dow: 0,
  },
});

export class LocalesStore {
  @observable loading: boolean = false;
  @persist @observable language: TLocale =
    process.env.NODE_ENV === 'production' && window.SETTINGS ? window.SETTINGS?.default_language : 'en';
  @persist @observable timestamp: null | number = null;
  @persist('object') @observable locales: { en: any; ar: any } = { en: null, ar: null };

  @computed get direction() {
    return this.language === 'ar' ? 'rtl' : 'ltr';
  }

  // actions

  @action fetchLocale = async () => {
    try {
      this.loading = true;
      const locale = await requests.fetchLocales(this.language);
      this.locales[this.language] = locale;
      this.timestamp = Date.now();
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logError(error);
    }
  };

  @action fetchLocales = async () => {
    try {
      const secondLanguage = this.language === 'en' ? 'ar' : 'en';
      this.loading = true;
      const locale = await requests.fetchLocales(this.language);
      this.locales[this.language] = locale;
      this.timestamp = Date.now();

      const localeSecond = await requests.fetchLocales(secondLanguage);
      this.locales[secondLanguage] = localeSecond;
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logError(error);
    }
  };

  @action public setLocale(language: TLocale, shouldChange: boolean = true) {
    const isChanged = language !== this.language;

    logger.logEvent(EVENTS.APP_LANGUAGE_CHANGES, {
      language_from: this.language,
      language_to: language,
    });

    cookies.setCookie('language', language);
    this.language = language;
    client.headers = {
      'Accept-Language': language,
    };

    if (isChanged && shouldChange) {
      return (window.location.href = window.location.origin);
    }

    this.fetchLocale();
    productsStore.fetchMenu();
  }

  @action public t = (key: string, options: any = {}): string => {
    if (!this.locales || !this.locales[this.language]) return key;
    let translate = this.locales[this.language][key] || key;
    if (options) {
      translate = Object.entries(options).reduce((_translate, [_key, value]) => {
        return _translate.replace(`{{${_key}}}`, value);
      }, translate);
    }
    return translate;
  };

  afterHydration() {
    if (!this.timestamp) {
      this.fetchLocales();
    } else {
      this.fetchLocale();
    }

    cookies.setCookie('language', this.language);
    client.headers = {
      'Accept-Language': this.language,
    };

    moment.locale(this.language === 'ar' ? 'ar-custom' : this.language);
  }
}

const localeStore = new LocalesStore();

export { localeStore };
